import { useEffect, useMemo, useRef, useState } from 'react'
import styles from './AllFiltersDrawer.module.css'
import CountPill from './CountPill'
import FilterGroupView, { isGroupHidden } from './FilterGroupView'
import RatingsPanel from './RatingsPanel'
import { searchFilterOptions } from 'lib/dealhunt/client'
import { FILTER_SEARCH_DEBOUNCE_MS, FILTER_SEARCH_MIN_CHARS } from 'lib/dealhunt/constants'
import {
  applyParamRef,
  clearAllFilters,
  isParamRefSelected,
  type CategoryFilterState,
} from 'lib/dealhunt/filters'
import {
  isScoreFilterGroup,
  type CategoryFilterModel,
  type CategorySummary,
  type FilterGroup,
  type FilterSearchResults,
  type ScoreFilterGroup,
} from 'lib/dealhunt/types'

interface AllFiltersDrawerProps {
  open: boolean
  /** Scopes the Meili filter search to this category's `sg_filter_options` rows. */
  categoryNodeId: string
  model: CategoryFilterModel
  summary: CategorySummary
  filters: CategoryFilterState
  categoryName: string
  onChange: (next: CategoryFilterState) => void
  onClose: () => void
  /** `model.more` is still on its way (SSR deferred it — see `deferDrawerModel`). */
  moreLoading?: boolean
}

/** The ratings panel has no group in the model; it matches the search on its own title. */
const RATINGS_TITLE = 'soundguys ratings'

/** What the drawer's Tab trap cycles through. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** NUL: can't occur in a group id or an option value, so the composite key never collides. */
const LIVE_KEY_SEP = String.fromCharCode(0)

function groupMatches(group: FilterGroup | ScoreFilterGroup, q: string): boolean {
  if (isGroupHidden(group)) {
    return false
  }
  if (!q) {
    return true
  }
  if (group.label.toLowerCase().includes(q)) {
    return true
  }
  if (isScoreFilterGroup(group) || group.control === 'range') {
    return false
  }
  return group.options.some(o => o.label.toLowerCase().includes(q))
}

/** Composite key of one option in the live lookup — written and read through here only. */
function liveKey(groupId: string, value: string): string {
  return groupId + LIVE_KEY_SEP + value
}

/** Live option lookup: the index says WHAT matches, the model says HOW MANY right now. */
function indexModelOptions(
  model: CategoryFilterModel
): Map<string, { count: number; selected: boolean }> {
  const map = new Map<string, { count: number; selected: boolean }>()
  const put = (groupId: string, value: string, count: number, selected: boolean): void => {
    map.set(liveKey(groupId, value), { count, selected })
  }
  const groups: (FilterGroup | ScoreFilterGroup)[] = [
    ...model.featured,
    ...model.more.flatMap(sec => sec.groups),
  ]
  for (const g of groups) {
    if (isScoreFilterGroup(g) || g.control === 'range') {
      continue
    }
    for (const o of g.options) {
      put(g.id, o.value, o.count, o.selected)
    }
    if (g.control === 'pills' && g.extras) {
      for (const o of g.extras) {
        put(g.id, o.value, o.count, o.selected)
      }
    }
    if (g.control === 'dist' && g.other) {
      put(g.id, g.other.value, g.other.count, g.other.selected)
    }
  }
  for (const o of model.verdict.options) {
    put('verdict', o.value, o.count, o.selected)
  }
  return map
}

/**
 * "All filters": a right-hand drawer on desktop, a bottom sheet on mobile. The
 * whole model lives here — ratings, featured, everything else — with search
 * across group labels and values, the model's "Popular" shortcuts, and a sticky
 * "Show N products" CTA. The same element serves both breakpoints (CSS decides).
 */
const AllFiltersDrawer: React.VFC<AllFiltersDrawerProps> = ({
  open,
  categoryNodeId,
  model,
  summary,
  filters,
  categoryName,
  onChange,
  onClose,
  moreLoading = false,
}) => {
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const [results, setResults] = useState<FilterSearchResults | null>(null)
  const searchAbort = useRef<AbortController | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  // Latest close handler without re-running the focus effect (the template passes
  // an inline arrow, so a dependency on it would re-fire on every render).
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // Meili-backed filter search (sg_filter_options): debounced, abortable, scoped to
  // the category. Null results (Meili unconfigured / failed) leave the local label
  // match below as the fallback — the drawer never goes empty because search is down.
  useEffect(() => {
    searchAbort.current?.abort()
    if (!open || q.length < FILTER_SEARCH_MIN_CHARS) {
      setResults(null)
      return undefined
    }
    const ctrl = new AbortController()
    searchAbort.current = ctrl
    const timer = setTimeout(() => {
      searchFilterOptions(categoryNodeId, query.trim(), ctrl.signal)
        .then(res => {
          if (!ctrl.signal.aborted) {
            setResults(res)
          }
        })
        .catch(() => {
          /* aborted or failed — keep the previous/local results */
        })
    }, FILTER_SEARCH_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [open, q, query, categoryNodeId])

  const live = useMemo(() => indexModelOptions(model), [model])

  // Modal behaviour while open: focus moves into the panel, Tab cycles inside it,
  // Escape closes, the page doesn't scroll behind, and focus returns to the opener
  // (the "Filters" / "All filters" door) on close.
  useEffect(() => {
    if (!open) {
      return undefined
    }
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const panel = panelRef.current
    panel?.focus()
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onCloseRef.current()
        return
      }
      if (e.key !== 'Tab' || !panel) {
        return
      }
      const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (focusable.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const current = document.activeElement
      if (e.shiftKey && (current === first || current === panel)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && current === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
      opener?.focus()
    }
  }, [open])

  const featured = useMemo(
    () => model.featured.filter(g => groupMatches(g, q)),
    [model.featured, q]
  )
  const more = useMemo(
    () =>
      model.more
        .map(section => ({ ...section, groups: section.groups.filter(g => groupMatches(g, q)) }))
        .filter(section => section.groups.length > 0),
    [model.more, q]
  )
  const suggest = useMemo(
    () => (q ? model.suggest.filter(s => s.label.toLowerCase().includes(q)) : model.suggest),
    [model.suggest, q]
  )

  if (!open) {
    return null
  }
  // Index search surfaces VALUES only: a hit is something to apply, never a "go to"
  // row — the group/score labels already match locally below.
  const hasIndexHits = results !== null && results.values.length > 0
  const showRatings = q === '' || RATINGS_TITLE.includes(q)
  const nothing = !hasIndexHits && featured.length === 0 && more.length === 0 && !showRatings

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="All filters"
      >
        <header className={styles.head}>
          <span className={styles.title}>{'All filters'}</span>
          <span className={styles.sub}>
            {`${summary.moreTotal} filters across ${
              summary.moreGroups
            } groups · ${categoryName.toLowerCase()}`}
          </span>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close filters"
          >
            {'×'}
          </button>
        </header>
        <div className={styles.body}>
          <input
            type="search"
            className={styles.search}
            placeholder="Search filters and values"
            value={query}
            aria-label="Search filters"
            onChange={e => setQuery(e.target.value)}
          />
          {hasIndexHits && results && (
            <>
              <div className={styles.sectionLabel}>{'Values'}</div>
              <div className={styles.suggest}>
                {results.values.map(hit => {
                  const state = live.get(liveKey(hit.groupId, hit.value))
                  const selected = state?.selected ?? isParamRefSelected(filters, hit.param)
                  return (
                    <CountPill
                      key={hit.id}
                      label={hit.label}
                      highlight={query}
                      count={state ? state.count : null}
                      selected={selected}
                      title={hit.groupLabel}
                      onClick={() => onChange(applyParamRef(filters, hit.param, !selected))}
                    />
                  )
                })}
              </div>
            </>
          )}
          {suggest.length > 0 && !(results && q) && (
            <>
              <div className={styles.sectionLabel}>{q ? 'Matching values' : 'Popular'}</div>
              <div className={styles.suggest}>
                {suggest.map(s => {
                  const selected = isParamRefSelected(filters, s.param)
                  return (
                    <CountPill
                      key={`${s.groupId}:${s.value}`}
                      label={s.label}
                      count={s.count}
                      selected={selected}
                      onClick={() => onChange(applyParamRef(filters, s.param, !selected))}
                    />
                  )
                })}
              </div>
            </>
          )}
          {showRatings && (
            <>
              <div className={styles.sectionLabel}>{'SoundGuys ratings'}</div>
              <RatingsPanel
                model={model}
                summary={summary}
                filters={filters}
                onChange={onChange}
                compact
              />
            </>
          )}
          {featured.length > 0 && (
            <>
              {q === '' && <div className={styles.sectionLabel}>{'Featured filters'}</div>}
              {featured.map(group => (
                <FilterGroupView
                  key={group.id}
                  group={group}
                  filters={filters}
                  onChange={onChange}
                  variant="row"
                />
              ))}
            </>
          )}
          {more.length > 0 && q === '' && (
            <div className={styles.sectionLabel}>{'Everything else'}</div>
          )}
          {moreLoading && more.length === 0 && (
            <div className={styles.empty} role="status">
              {'Loading all filters…'}
            </div>
          )}
          {more.map(section => (
            <div key={section.id} className={styles.moreSection}>
              <h3 className={styles.moreTitle}>{section.title}</h3>
              {section.groups.map(group => (
                <FilterGroupView
                  key={group.id}
                  group={group}
                  filters={filters}
                  onChange={onChange}
                  variant="row"
                />
              ))}
            </div>
          ))}
          {nothing && !moreLoading && (
            <div className={styles.empty}>{'No filters match that search.'}</div>
          )}
        </div>
        <footer className={styles.foot}>
          <span className={styles.footNote}>
            {'Counts update as you filter. A value showing 0 has nothing left to match.'}
          </span>
          <button
            type="button"
            className={styles.clear}
            onClick={() => onChange(clearAllFilters(filters))}
          >
            {'Clear'}
          </button>
          <button type="button" className={styles.cta} onClick={onClose}>
            {`Show ${summary.matched.toLocaleString('en-US')} products`}
          </button>
        </footer>
      </div>
    </>
  )
}

export default AllFiltersDrawer
